import { StringContext } from "../../../core/StringContext";
import { StringModule } from "../../../core/StringModule";
import {
  StringDevtoolDefinition,
  StringDevtoolProvider,
  StringDevtoolState,
} from "../../../models/devtools/StringDevtool";
import { resolveDevtoolsIcon } from "./StringDevIconRegistry";
import { StringDevOverlayRegistry } from "./StringDevOverlayRegistry";
import { ensureStringDevtoolsSharedStyles } from "./StringDevStyleSystem";
import { StringDevViewportLayer } from "./StringDevViewportLayer";

export interface StringDevModuleDefinitionConfig {
  id: string;
  label: string;
  icon: string;
  order?: number;
}

export interface StringDevOverlayConfig {
  /** Unique layer name, e.g. "devtools-progress" */
  layerName: string;
  /** Z-index for the viewport layer */
  zIndex: number;
  /** Data-attribute added to the layer root, e.g. "data-string-progress-layer" */
  layerAttribute?: string;
  /** Overlay ID used for the anchor/collision system. Omit if not needed. */
  overlayId?: string;
  /** Whether the overlay starts enabled before dock preferences apply. Default: false */
  defaultEnabled?: boolean;
  /** For badge overlay modules: data-attribute placed on each badge element */
  badgeAttribute?: string;
}

export interface StringDevModuleConfig extends StringDevModuleDefinitionConfig {
  /** Declare CSS styles to inject once. Can be a string or a lazy getter. */
  styles?: string | (() => string);
  /** Overlay layer configuration. Declare for any module that uses a viewport layer. */
  overlay?: StringDevOverlayConfig;
}

export class StringDevModule extends StringModule implements StringDevtoolProvider {
  /** Declare module metadata statically. Subclasses override this. */
  static devtool: StringDevModuleConfig | null = null;

  protected readonly overlayRegistry = StringDevOverlayRegistry.getInstance();

  private readonly acquiredViewportLayers = new Map<string, StringDevViewportLayer>();
  private readonly devtoolListeners = new Set<(state: StringDevtoolState) => void>();
  protected devtoolConfig: StringDevModuleDefinitionConfig | null = null;

  constructor(context: StringContext) {
    super(context);
    this._type = 2;
    ensureStringDevtoolsSharedStyles();

    const staticConfig = (this.constructor as typeof StringDevModule).devtool;
    if (staticConfig) {
      this.configureDevtool(staticConfig);
    }

    const rawStaticStyles = staticConfig?.styles;
    const styles = (typeof rawStaticStyles === "function" ? rawStaticStyles() : rawStaticStyles) ?? this.getStyles();
    if (styles) {
      this.ensureStyle(`${this.getStyleScopeId(styles)}-styles`, styles);
    }
  }

  protected getStyleScopeId(stylesText: string): string {
    if (this.htmlKey) {
      return this.htmlKey;
    }

    let hash = 0;
    for (let i = 0; i < stylesText.length; i += 1) {
      hash = (hash * 31 + stylesText.charCodeAt(i)) >>> 0;
    }

    return `string-dev-${hash.toString(16)}`;
  }

  protected getStyles(): string | null {
    return null;
  }

  public getDevtoolDefinition(): StringDevtoolDefinition | null {
    if (!this.devtoolConfig) {
      return null;
    }

    const config = this.devtoolConfig;
    return {
      id: config.id,
      label: config.label,
      icon: resolveDevtoolsIcon(config.icon),
      order: config.order,
      getState: () => ({ active: this.getDevtoolActiveState() }),
      setActive: (active) => {
        this.setDevtoolActiveState(active);
      },
      subscribe: (listener) => {
        this.devtoolListeners.add(listener);
        listener({ active: this.getDevtoolActiveState() });
        return () => {
          this.devtoolListeners.delete(listener);
        };
      },
    };
  }

  protected configureDevtool(config: StringDevModuleDefinitionConfig): void {
    this.devtoolConfig = config;
  }

  protected emitDevtoolState(active: boolean = this.getDevtoolActiveState()): void {
    const state = { active };
    for (const listener of this.devtoolListeners) {
      listener(state);
    }
  }

  protected acquireViewportLayer(id: string, zIndex: number): StringDevViewportLayer {
    const existing = this.acquiredViewportLayers.get(id);
    if (existing) {
      return existing;
    }

    const layer = this.overlayRegistry.acquire(id, zIndex);
    this.acquiredViewportLayers.set(id, layer);
    return layer;
  }

  protected releaseViewportLayer(id: string): void {
    if (!this.acquiredViewportLayers.has(id)) {
      return;
    }

    this.overlayRegistry.release(id);
    this.acquiredViewportLayers.delete(id);
  }

  protected ensureStyle(styleId: string, cssText: string): HTMLStyleElement {
    const existing = document.getElementById(styleId);
    if (existing instanceof HTMLStyleElement) {
      return existing;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = cssText;
    document.head.appendChild(style);
    return style;
  }

  protected getWorldHost(): HTMLElement {
    return this.data.scroll.container ?? document.body ?? document.documentElement;
  }

  protected getDevtoolActiveState(): boolean {
    return false;
  }

  protected setDevtoolActiveState(_active: boolean): void {}

  destroy(): void {
    this.devtoolListeners.clear();
    for (const id of this.acquiredViewportLayers.keys()) {
      this.overlayRegistry.release(id);
    }
    this.acquiredViewportLayers.clear();
    super.destroy();
  }
}
